// Cache tags for the generated cycle endpoints, kept separate from the api wiring in
// `cyclesEnhanced.ts` so the policy can be read and tested without pulling in the whole store.
// Deliberately import-free.

// Courses carry active_cycle_count, a server-side count of running cycles. Which writes move it
// depends on date arithmetic and a delete guard this layer cannot see, so every write invalidates
// it rather than the client guessing.
const TENANT_COURSES_LIST = { type: 'TenantCourses' as const, id: 'LIST' }
const tenantCourse = (courseCode: string) => ({ type: 'TenantCourses' as const, id: courseCode })

export const cycleCacheTags = {
  listTrainingCycles: {
    providesTags: [{ type: 'TrainingCycles' as const, id: 'LIST' }]
  },
  listTenantTrainingCycles: {
    providesTags: [{ type: 'TrainingCycles' as const, id: 'LIST' }]
  },
  getTrainingCycle: {
    providesTags: (_result: unknown, _error: unknown, arg: { cycleId: string }) => [
      { type: 'TrainingCycles' as const, id: arg.cycleId }
    ]
  },
  createTrainingCycle: {
    invalidatesTags: (_result: unknown, _error: unknown, arg: { courseCode: string }) => [
      { type: 'TrainingCycles' as const, id: 'LIST' },
      TENANT_COURSES_LIST,
      tenantCourse(arg.courseCode)
    ]
  },
  updateTrainingCycle: {
    invalidatesTags: (_result: unknown, _error: unknown, arg: { cycleId: string; courseCode: string }) => [
      { type: 'TrainingCycles' as const, id: 'LIST' },
      { type: 'TrainingCycles' as const, id: arg.cycleId },
      TENANT_COURSES_LIST,
      tenantCourse(arg.courseCode)
    ]
  },
  deleteTrainingCycle: {
    invalidatesTags: (_result: unknown, _error: unknown, arg: { cycleId: string; courseCode: string }) => [
      { type: 'TrainingCycles' as const, id: 'LIST' },
      { type: 'TrainingCycles' as const, id: arg.cycleId },
      TENANT_COURSES_LIST,
      tenantCourse(arg.courseCode)
    ]
  },
  listEnrollments: {
    providesTags: (_result: unknown, _error: unknown, arg: { cycleId: string }) => [
      { type: 'CycleEnrollments' as const, id: arg.cycleId }
    ]
  },
  // Same tag as listEnrollments: a create/remove can change which departments, sources etc are
  // present on the cycle, so both reads invalidate together.
  listEnrollmentFilterOptions: {
    providesTags: (_result: unknown, _error: unknown, arg: { cycleId: string }) => [
      { type: 'CycleEnrollments' as const, id: arg.cycleId }
    ]
  },
  // Enrolling and unenrolling both move the list DTO's completion counts, so the cycles list is
  // invalidated alongside the cycle's own enrollment page.
  createEnrollments: {
    invalidatesTags: (_result: unknown, _error: unknown, arg: { cycleId: string }) => [
      { type: 'CycleEnrollments' as const, id: arg.cycleId },
      { type: 'TrainingCycles' as const, id: 'LIST' }
    ]
  },
  removeEnrollments: {
    invalidatesTags: (_result: unknown, _error: unknown, arg: { cycleId: string }) => [
      { type: 'CycleEnrollments' as const, id: arg.cycleId },
      { type: 'TrainingCycles' as const, id: 'LIST' }
    ]
  },
  // The override writes a completion, which moves the learner's status on the roster and the
  // cycle's completed count on the list DTO - the same two reads unenrolling moves.
  forceCompleteEnrollment: {
    invalidatesTags: (_result: unknown, _error: unknown, arg: { cycleId: string }) => [
      { type: 'CycleEnrollments' as const, id: arg.cycleId },
      { type: 'TrainingCycles' as const, id: 'LIST' }
    ]
  },
  // The batch forms of the two above. No caller yet, but they arrived with the regenerated client
  // and an untagged mutation fails silently - the first caller would see a stale roster with no
  // clue why - so they are tagged now rather than left as a trap. Each entry names its own cycle,
  // so the invalidation stays per-cycle instead of dropping every cycle's enrollments.
  createEnrollmentsBatch: {
    invalidatesTags: (_result: unknown, _error: unknown, arg: { body: { cycle_id: string }[] }) => [
      ...arg.body.map((entry) => ({ type: 'CycleEnrollments' as const, id: entry.cycle_id })),
      { type: 'TrainingCycles' as const, id: 'LIST' }
    ]
  },
  removeEnrollmentsBatch: {
    invalidatesTags: (_result: unknown, _error: unknown, arg: { body: { cycle_id: string }[] }) => [
      ...arg.body.map((entry) => ({ type: 'CycleEnrollments' as const, id: entry.cycle_id })),
      { type: 'TrainingCycles' as const, id: 'LIST' }
    ]
  },
  // The 202 only queues the mail, so enrollment fields like last_notified_at have not moved yet -
  // but the cooldown claim is written before the response returns, which changes the cycle's own
  // manual_send_available_at. Without this the send button stays live on a list that has already
  // gone stale, and the next click is refused by the API the UI just stopped agreeing with.
  sendCycleReminder: {
    invalidatesTags: (_result: unknown, _error: unknown, arg: { cycleId: string }) => [
      { type: 'TrainingCycles' as const, id: 'LIST' },
      { type: 'TrainingCycles' as const, id: arg.cycleId }
    ]
  },
  // The 202 creates the request row the status read reports, so has_active_export flips server-side
  // before the response returns. Tags only fire on success: the 409 (an export started elsewhere is
  // still running) leaves the cache untouched, so that path still needs a manual refetch.
  getMyTrainingRecordsExportStatus: {
    providesTags: [{ type: 'TrainingRecordsExport' as const, id: 'STATUS' }]
  },
  exportTrainingRecordsCsv: {
    invalidatesTags: [{ type: 'TrainingRecordsExport' as const, id: 'STATUS' }]
  },
  exportGlobalTrainingRecordsCsv: {
    invalidatesTags: [{ type: 'TrainingRecordsExport' as const, id: 'STATUS' }]
  }
} as const
